import * as THREE from "three";
import { colors } from "./colors";

export class BoundingBoxManager {
  constructor(scene, renderer, camera, options = {}) {
    this.scene = scene;
    this.renderer = renderer;
    this.camera = camera;
    this.color = new THREE.Color(options.color || colors.blue);
    this.thickness = options.thickness || 0.01;
    this.glowIntensity = options.glowIntensity || 1.0;
    this.outlines = new Map();
  }

  getTopRightScreenPosition(object) {
    object.updateWorldMatrix(true, false);

    object.geometry.computeBoundingBox();
    const bbox = object.geometry.boundingBox.clone();

    const corners = [
      new THREE.Vector3(bbox.min.x, bbox.min.y, bbox.min.z),
      new THREE.Vector3(bbox.max.x, bbox.min.y, bbox.min.z),
      new THREE.Vector3(bbox.max.x, bbox.max.y, bbox.min.z),
      new THREE.Vector3(bbox.min.x, bbox.max.y, bbox.min.z),
      new THREE.Vector3(bbox.min.x, bbox.min.y, bbox.max.z),
      new THREE.Vector3(bbox.max.x, bbox.min.y, bbox.max.z),
      new THREE.Vector3(bbox.max.x, bbox.max.y, bbox.max.z),
      new THREE.Vector3(bbox.min.x, bbox.max.y, bbox.max.z),
    ];

    const canvas = this.renderer.domElement;
    const camera = this.camera;

    // world coords converted to camera's local coords
    const camMatrixWorldInverse = camera.matrixWorldInverse.clone();

    let bestCorner = null;
    let bestScore = -Infinity;

    for (let c of corners) {
      //transform coord from local to world
      //worldCorner holds bbox corner in world space
      const worldCorner = c.clone().applyMatrix4(object.matrixWorld);

      // transform world bbox corner to camera's local space coord
      const camSpace = worldCorner.clone().applyMatrix4(camMatrixWorldInverse);

      // prioritize upper-right corner from camera POV (high x, high y in cam space)
      const score = camSpace.x + camSpace.y;

      if (score > bestScore) {
        bestScore = score;

        // now get screen position of worldCorner
        bestCorner = worldCorner.clone().project(camera);
      }
    }

    if (!bestCorner) return null;

    const x = (bestCorner.x * 0.5 + 0.5) * canvas.clientWidth;
    const y = (-bestCorner.y * 0.5 + 0.5) * canvas.clientHeight;

    return { x, y };
  }

  add(object) {
    this.remove(object);

    // if object is scaled, bounding box will be double
    // so we need to recompute world matrix
    object.updateWorldMatrix(true, false);

    //computes the bbox in local space before any transforms like position, rotation, or scale are applied
    object.geometry.computeBoundingBox();

    //clone bbox to avoid modifying the original geometry data
    const box = object.geometry.boundingBox.clone();

    const corners = [
      new THREE.Vector3(box.min.x, box.min.y, box.min.z),
      new THREE.Vector3(box.max.x, box.min.y, box.min.z),
      new THREE.Vector3(box.max.x, box.max.y, box.min.z),
      new THREE.Vector3(box.min.x, box.max.y, box.min.z),
      new THREE.Vector3(box.min.x, box.min.y, box.max.z),
      new THREE.Vector3(box.max.x, box.min.y, box.max.z),
      new THREE.Vector3(box.max.x, box.max.y, box.max.z),
      new THREE.Vector3(box.min.x, box.max.y, box.max.z),
    ];

    const edges = [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0], // bottom
      [4, 5],
      [5, 6],
      [6, 7],
      [7, 4], // top
      [0, 4],
      [1, 5],
      [2, 6],
      [3, 7], // sides
    ];

    const material = new THREE.MeshStandardMaterial({
      color: this.color,
      emissive: this.color.clone().multiplyScalar(this.glowIntensity),
      emissiveIntensity: this.glowIntensity,
    });

    const group = new THREE.Group();

    for (let [startIdx, endIdx] of edges) {
      const start = corners[startIdx];
      const end = corners[endIdx];
      const edgeVec = new THREE.Vector3().subVectors(end, start);
      const length = edgeVec.length();

      const geometry = new THREE.BoxGeometry(
        this.thickness,
        this.thickness,
        length,
      );
      const mesh = new THREE.Mesh(geometry, material);

      // Midpoint position
      const mid = new THREE.Vector3()
        .addVectors(start, end)
        .multiplyScalar(0.5);
      mesh.position.copy(mid);

      // Rotate to align with edge
      mesh.quaternion.setFromUnitVectors(
        new THREE.Vector3(0, 0, 1), // Default box z-axis
        edgeVec.clone().normalize(),
      );

      group.add(mesh);
    }

    object.add(group);

    this.outlines.set(object, group);
  }

  remove(object) {
    const group = this.outlines.get(object);
    if (group) {
      object.remove(group); // changed from scene.remove
      group.children.forEach((c) => {
        c.geometry.dispose();
        c.material.dispose();
      });
      this.outlines.delete(object);
    }
  }

  clear() {
    for (let obj of this.outlines.keys()) {
      this.remove(obj);
    }
  }
}
